import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../config/firebase';
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '@vortx/shared-ui';
import { getPageName, getPageCategory } from '@vortx/shared-ui';

// Constants
const HEARTBEAT_INTERVAL = 60000; // 1 minute
const IDLE_THRESHOLD = 120000; // 2 minutes
const SESSION_KEY = 'vortx_activity_session_id';

// Helper functions
function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function getSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

// Context
const ActivityContext = createContext();

export function useActivity() {
  return useContext(ActivityContext);
}

export function ActivityProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();

  const [isIdle, setIsIdle] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);
  const currentPathRef = useRef(location.pathname);
  const sessionIdRef = useRef(getSessionId());

  // Get denormalized user info for Firestore documents
  const getUserInfo = useCallback(() => {
    if (!currentUser || !userProfile) {
      return null;
    }
    return {
      user_id: currentUser.uid,
      user_email: currentUser.email,
      user_name: userProfile.name || userProfile.email || currentUser.email,
      company_id: userProfile.company_id || null,
      company_name: userProfile.company_name || null,
    };
  }, [currentUser, userProfile]);

  // Update user presence in Firestore
  // Only update current_page when this tab is visible to prevent tab-switching flicker
  const updatePresence = useCallback(async (status, options = {}) => {
    if (!currentUser) return;

    const userInfo = getUserInfo();
    if (!userInfo) return;

    // Only update page info if this tab is visible (or explicitly requested)
    const isVisible = document.visibilityState === 'visible';
    const shouldUpdatePage = options.forcePageUpdate || isVisible;

    try {
      const presenceRef = doc(db, 'user_presence', currentUser.uid);
      const presenceData = {
        ...userInfo,
        status,
        last_heartbeat: serverTimestamp(),
        session_id: sessionIdRef.current,
        updated_at: serverTimestamp(),
      };

      // Only include page info if this tab is visible
      // This prevents hidden tabs from overwriting the active tab's page
      if (shouldUpdatePage) {
        presenceData.current_page = currentPathRef.current;
        presenceData.current_page_name = getPageName(currentPathRef.current);
      }

      await setDoc(presenceRef, presenceData, { merge: true });
    } catch (error) {
      // Silently ignore permission errors - activity tracking is optional
      // Only log unexpected errors in development
      if (error?.code !== 'permission-denied' && process.env.NODE_ENV === 'development') {
        console.warn('[ActivityContext] Presence update failed:', error?.code || error);
      }
    }
  }, [currentUser, getUserInfo]);

  // Log activity event to Firestore
  const logEvent = useCallback(async (eventType, category, action, metadata = {}) => {
    if (!currentUser) return;

    const userInfo = getUserInfo();
    if (!userInfo) return;

    try {
      const eventsRef = collection(db, 'activity_events');
      await addDoc(eventsRef, {
        ...userInfo,
        event_type: eventType,
        category,
        action,
        metadata,
        page_path: currentPathRef.current,
        page_name: getPageName(currentPathRef.current),
        session_id: sessionIdRef.current,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      // Silently ignore permission errors - activity tracking is optional
      // Only log unexpected errors in development
      if (error?.code !== 'permission-denied' && process.env.NODE_ENV === 'development') {
        console.warn('[ActivityContext] Event logging failed:', error?.code || error);
      }
    }
  }, [currentUser, getUserInfo]);

  // Track user action (exposed to components)
  const trackAction = useCallback((eventType, category, action, metadata = {}) => {
    // Reset idle state
    lastActivityRef.current = Date.now();
    setIsIdle(false);

    // Log the event
    logEvent(eventType, category, action, metadata);
  }, [logEvent]);

  // Handle user activity (mouse/keyboard)
  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (isIdle) {
      setIsIdle(false);
      updatePresence('active');
    }
  }, [isIdle, updatePresence]);

  // Check if user is idle
  const checkIdle = useCallback(() => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;
    if (timeSinceLastActivity >= IDLE_THRESHOLD && !isIdle) {
      setIsIdle(true);
      updatePresence('idle');
    }
  }, [isIdle, updatePresence]);

  // Effect for page navigation tracking
  useEffect(() => {
    if (!currentUser) return;

    const newPath = location.pathname;
    if (newPath !== currentPathRef.current) {
      currentPathRef.current = newPath;

      // Log page view event
      logEvent('page_view', getPageCategory(newPath), 'navigate', {
        previous_page: currentPathRef.current,
        new_page: newPath,
      });

      // Update presence with new page
      updatePresence('active');
    }
  }, [location.pathname, currentUser, logEvent, updatePresence]);

  // Effect for heartbeat and activity listeners
  useEffect(() => {
    if (!currentUser) return;

    // Set initial presence
    updatePresence('active');

    // Log session start
    logEvent('session', 'system', 'session_start', {
      session_id: sessionIdRef.current,
    });

    // Set up heartbeat interval
    heartbeatIntervalRef.current = setInterval(() => {
      checkIdle();
      updatePresence(isIdle ? 'idle' : 'active');
    }, HEARTBEAT_INTERVAL);

    // Activity listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Visibility change handler
    // Note: We don't change status to 'away' when tab is hidden because:
    // 1. User may have multiple tabs open - other tabs are still active
    // 2. The idle detection (2 min threshold) will naturally handle inactive users
    // 3. Setting 'away' causes flickering in admin monitors when switching tabs
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastActivityRef.current = Date.now();
        setIsIdle(false);
        updatePresence('active');
      }
      // Don't set 'away' when hidden - let idle detection handle it
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Before unload handler
    // Note: We don't set offline on tab close because:
    // 1. User may have multiple tabs open - closing one shouldn't mark them offline
    // 2. Server-side cleanup should handle stale presence based on last_heartbeat
    // 3. Setting offline immediately causes flickering in admin monitors
    const handleBeforeUnload = () => {
      // Just update the heartbeat timestamp, don't change status
      // This allows admin monitors to detect stale sessions
      updatePresence(isIdle ? 'idle' : 'active');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Note: We don't set offline on unmount because:
      // 1. The component unmounts on navigation, not just when closing the tab
      // 2. User may still be active in other tabs
      // 3. Server-side cleanup should handle stale presence
    };
  }, [currentUser, updatePresence, logEvent, checkIdle, handleUserActivity, isIdle]);

  const value = {
    trackAction,
    isIdle,
    sessionId: sessionIdRef.current,
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
}
